import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import type {
  MailboxConnectionListResponse,
  MailboxConnection,
  CreateMailboxConnectionRequest,
  UpdateMailboxConnectionRequest,
  MailboxOAuthUrlResponse,
  MailboxFolderListResponse,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

// ===== Queries =====

export const useMailboxConnections = () => {
  return useQuery({
    queryKey: ['mailbox-connections'],
    queryFn: async () => {
      const { data } = await apiClient.get<MailboxConnectionListResponse>(
        '/mailbox/connections'
      );
      return data;
    },
  });
};

export const useMailboxFolders = (connectionId: string | null) => {
  return useQuery({
    queryKey: ['mailbox-folders', connectionId],
    queryFn: async () => {
      const { data } = await apiClient.get<MailboxFolderListResponse>(
        `/mailbox/connections/${connectionId}/folders`
      );
      return data;
    },
    enabled: !!connectionId,
  });
};

export const useMailboxOAuthUrl = (provider: string) => {
  return useQuery({
    queryKey: ['mailbox-oauth-url', provider],
    queryFn: async () => {
      const { data } = await apiClient.get<MailboxOAuthUrlResponse>(
        `/auth/mailbox/${provider}/url`
      );
      return data;
    },
    enabled: false,
  });
};

// ===== Mutations =====

export const useCreateMailboxConnection = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: CreateMailboxConnectionRequest) => {
      const { data } = await apiClient.post<MailboxConnection>(
        '/mailbox/connections',
        payload
      );
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'Mailbox connection created successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['mailbox-connections'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail || 'Failed to create mailbox connection',
        type: 'error',
      });
    },
  });
};

export const useUpdateMailboxConnection = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: UpdateMailboxConnectionRequest & { id: string }) => {
      const { data } = await apiClient.patch<MailboxConnection>(
        `/mailbox/connections/${id}`,
        payload
      );
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'Mailbox connection updated',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['mailbox-connections'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail || 'Failed to update mailbox connection',
        type: 'error',
      });
    },
  });
};

export const useDeleteMailboxConnection = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/mailbox/connections/${id}`);
    },
    onSuccess: () => {
      addNotification({
        message: 'Mailbox connection removed',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['mailbox-connections'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail || 'Failed to remove mailbox connection',
        type: 'error',
      });
    },
  });
};

export const usePauseMailboxConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<MailboxConnection>(
        `/mailbox/connections/${id}/pause`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailbox-connections'] });
    },
  });
};

export const useResumeMailboxConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<MailboxConnection>(
        `/mailbox/connections/${id}/resume`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailbox-connections'] });
    },
  });
};

export const usePollMailboxNow = () => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(`/mailbox/connections/${id}/poll-now`);
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'Mailbox poll triggered',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to trigger poll',
        type: 'error',
      });
    },
  });
};
